import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getSessionStatsList, getAggregatedStats, getDrillHistory } from '../lib/firestore';
import { calculateTier, progressToNextTier, generateInsights, DRILLS, DRILL_TIER_CONFIGS, getDrillTier } from '../shared';
import type { AggregatedStats, SessionStats, DrillAttempt, DrillType } from '../shared';
import { StatCard, Panel, InsightBox, TierBadge, EmptyState, LoadingSpinner } from '../components/ui/Cards';
import { Button } from '../components/ui/Button';
import { LanguageSelector } from '../components/ui/LanguageSelector';
import { DRILL_ICONS } from '../drillIcons';
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';

export function DashboardPage() {
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [aggStats, setAggStats]         = useState<AggregatedStats | null>(null);
  const [recentStats, setRecentStats]   = useState<SessionStats[]>([]);
  const [drillHistory, setDrillHistory] = useState<DrillAttempt[]>([]);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      getAggregatedStats(user.uid),
      getSessionStatsList(user.uid, 10),
      getDrillHistory(user.uid),
    ]).then(([agg, stats, drills]) => {
      setAggStats(agg);
      setRecentStats(stats.slice().reverse());
      setDrillHistory(drills);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user]);

  if (loading) return <LoadingSpinner />;

  const avgBreak   = aggStats?.avgBreak30d      ?? 0;
  const potRate    = aggStats?.avgPotRate30d     ?? 0;
  const safetyRate = aggStats?.avgSafetyRate30d  ?? 0;
  const highBreak  = aggStats?.highBreakAllTime  ?? 0;

  const currentTier = calculateTier(avgBreak, potRate, safetyRate);
  const { nextTier, progress } = progressToNextTier(avgBreak, potRate, safetyRate);
  const hasData = (aggStats?.sessionCount ?? 0) > 0 || recentStats.length > 0;

  const insights = recentStats[recentStats.length - 1]
    ? (generateInsights as Function)(recentStats[recentStats.length - 1], recentStats[recentStats.length - 2] ?? null)
    : [];

  const chartData = recentStats.map((s, i) => ({
    session: i + 1,
    highBreak: s.highBreak,
    avgBreak: s.avgBreak,
    potRate: s.potRate,
  }));

  // ── Aggregate specialty stats across recent sessions ───────────
  const totalSnookered    = recentStats.reduce((sum, s) => sum + ((s as any).snookeredCount    ?? 0), 0);
  const totalEscapes      = recentStats.reduce((sum, s) => sum + ((s as any).snookerEscapes    ?? 0), 0);
  const totalLongAttempts = recentStats.reduce((sum, s) => sum + ((s as any).totalLongAttempts ?? 0), 0);
  const totalLongPots     = recentStats.reduce((sum, s) => sum + ((s as any).totalLongPots     ?? 0), 0);
  const totalRestAttempts = recentStats.reduce((sum, s) => sum + ((s as any).totalRestAttempts ?? 0), 0);
  const totalRestPots     = recentStats.reduce((sum, s) => sum + ((s as any).totalRestPots     ?? 0), 0);

  const escapeRate     = totalSnookered    > 0 ? Math.round((totalEscapes   / totalSnookered)    * 100) : null;
  const longPotRate    = totalLongAttempts > 0 ? Math.round((totalLongPots  / totalLongAttempts) * 100) : null;
  const restSuccessRate= totalRestAttempts > 0 ? Math.round((totalRestPots  / totalRestAttempts) * 100) : null;

  // ── Drill recap ────────────────────────────────────────────────
  const drillRecap = DRILLS.map(drill => {
    const attempts = drillHistory.filter(d => d.drillId === drill.id);
    if (attempts.length === 0) return null;
    const scores  = attempts.map(a => Number(a.notes ?? 0)).filter(n => !isNaN(n));
    const best    = Math.max(...scores);
    const avg     = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    const tier    = getDrillTier(drill.id, best);
    const config  = DRILL_TIER_CONFIGS[drill.id];
    const isSafetyPct = config.scoreType === 'safety_pct';
    return { drill, best, avg, tier, attempts: attempts.length, isSafetyPct };
  }).filter(Boolean) as NonNullable<ReturnType<typeof DRILLS['map']>[number]>[];

  const hasDrillData = drillRecap.length > 0;

  return (
    <div className="page u-fade-up">
      <div className="page__header">
        <div>
          <p style={{ fontStyle: 'italic', color: 'var(--chalk-dim)', marginBottom: 4, fontSize: '0.85rem' }}>
            {t('dashboard.welcomeBack')}
          </p>
          <h1 className="page__title">{profile?.displayName ?? 'Player'}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <LanguageSelector />
          <TierBadge
            tierName={currentTier.name}
            tierColor={currentTier.color}
            nextTierName={nextTier?.name}
            progress={progress}
          />
        </div>
      </div>

      {!hasData ? (
        <EmptyState
          icon="🎱"
          title={t('dashboard.noSessions')}
          subtitle={t('dashboard.noSessionsSub')}
          action={<Button size="lg" onClick={() => navigate('/session')}>{t('dashboard.logFirstSession')}</Button>}
        />
      ) : (
        <>
          {/* ── Core stats ── */}
          <div className="u-stats-grid">
            <StatCard label={t('dashboard.potRate30d')}    value={`${potRate}%`}    sub={t('dashboard.trailingAvg')} />
            <StatCard label={t('dashboard.avgBreak30d')}   value={avgBreak}         sub={t('dashboard.thisPeriod')} />
            <StatCard label={t('dashboard.allTimeHigh')}   value={highBreak}        sub={t('dashboard.bestBreak')} />
            <StatCard label={t('dashboard.safetyRate30d')} value={`${safetyRate}%`} sub={t('dashboard.effectiveSafeties')} />
            <StatCard label={t('dashboard.sessions')}      value={aggStats?.sessionCount ?? recentStats.length} sub={t('dashboard.totalLogged')} />
          </div>

          {/* ── Specialty stats — only appear once you have data ── */}
          {(escapeRate !== null || longPotRate !== null || restSuccessRate !== null) && (
            <div className="u-stats-grid" style={{ marginBottom: 14 }}>
              {escapeRate !== null && (
                <StatCard
                  label="Escape Rate"
                  value={`${escapeRate}%`}
                  sub={`${totalEscapes}/${totalSnookered} snookers`}
                />
              )}
              {longPotRate !== null && (
                <StatCard
                  label="Long Pot Rate"
                  value={`${longPotRate}%`}
                  sub={`${totalLongPots}/${totalLongAttempts} attempts`}
                />
              )}
              {restSuccessRate !== null && (
                <StatCard
                  label="Rest Success"
                  value={`${restSuccessRate}%`}
                  sub={`${totalRestPots}/${totalRestAttempts} attempts`}
                />
              )}
            </div>
          )}

          <div className="u-two-col">
            {/* ── Tier progression ── */}
            <Panel title={t('dashboard.tierProgression')}>
              <ProgressRow label={t('common.avgBreak')} current={avgBreak}   target={nextTier?.minAvgBreak ?? avgBreak}     unit=""  color={currentTier.color} />
              <ProgressRow label={t('session.potRate')}  current={potRate}    target={nextTier?.minPotRate ?? potRate}       unit="%" color={currentTier.color} />
              <ProgressRow label={t('session.safety')}   current={safetyRate} target={nextTier?.minSafetyRate ?? safetyRate} unit="%" color={currentTier.color} />
              {nextTier ? (
                <p className="progress-row__hint" style={{ marginTop: 8 }}>
                  {t('dashboard.nextTier')} <span style={{ color: nextTier.color }}>{nextTier.name}</span> — {nextTier.description}
                </p>
              ) : (
                <p className="progress-row__hint" style={{ marginTop: 8, color: 'var(--gold)' }}>
                  {t('dashboard.maxTier')}
                </p>
              )}
            </Panel>

            {/* ── Break history ── */}
            <Panel
              title={t('dashboard.breakHistory')}
              action={<Button size="sm" variant="ghost" onClick={() => navigate('/progress')}>{t('dashboard.viewAll')} →</Button>}
            >
              {chartData.length > 0 ? (
                <>
                  <StatChart data={chartData} dataKey="highBreak" color={currentTier.color} label="High Break" />
                  <StatChart data={chartData} dataKey="avgBreak"  color="rgba(201,168,76,0.6)" label="Avg Break" refLine={avgBreak} />
                </>
              ) : (
                <p style={{ fontSize: '0.82rem', color: 'var(--chalk-faint)', fontStyle: 'italic' }}>
                  {t('dashboard.statsProcessing')}
                </p>
              )}
            </Panel>
          </div>

          {/* ── Drill Recap ── */}
          {hasDrillData && (
            <Panel
              title="Drill Performance"
              style={{ marginBottom: 14 }}
              action={<Button size="sm" variant="ghost" onClick={() => navigate('/progress')}>{t('dashboard.viewAll')} →</Button>}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                {(drillRecap as any[]).map(({ drill, best, avg, tier: drillTier, attempts, isSafetyPct }: any) => {
                  const DrillIcon = DRILL_ICONS[drill.id as DrillType];
                  return (
                    <div
                      key={drill.id}
                      style={{
                        background: drillTier ? `${drillTier.color}0e` : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${drillTier ? `${drillTier.color}30` : 'rgba(255,255,255,0.06)'}`,
                        borderRadius: 8, padding: '12px 14px',
                      }}
                    >
                      <div style={{ marginBottom: 6 }}>
                        <DrillIcon size={18} strokeWidth={1.5} color="var(--chalk-dim)" />
                      </div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--chalk-dim)', marginBottom: 8, letterSpacing: '0.02em' }}>
                        {drill.name}
                      </div>
                      {drillTier && (
                        <div style={{ display: 'inline-block', fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: drillTier.color, marginBottom: 8, background: `${drillTier.color}18`, borderRadius: 3, padding: '2px 6px' }}>
                          {drillTier.label}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 12 }}>
                        <div>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 800, color: drillTier?.color ?? 'var(--chalk)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                            {best}{isSafetyPct ? '%' : ''}
                          </div>
                          <div style={{ fontSize: '0.52rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--chalk-faint)', marginTop: 2 }}>Best</div>
                        </div>
                        <div>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 800, color: 'var(--chalk-dim)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                            {avg}{isSafetyPct ? '%' : ''}
                          </div>
                          <div style={{ fontSize: '0.52rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--chalk-faint)', marginTop: 2 }}>Avg</div>
                        </div>
                        <div>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 800, color: 'var(--chalk-dim)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                            {attempts}
                          </div>
                          <div style={{ fontSize: '0.52rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--chalk-faint)', marginTop: 2 }}>Runs</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          {/* ── Coaching insights ── */}
          {insights.length > 0 && (
            <Panel title={`${t('dashboard.coachingInsights')} — ${t('dashboard.lastSession')}`}>
              {insights.map((ins: any, i: number) => (
                <InsightBox key={i} icon={ins.icon} text={ins.text} action={ins.action} onAction={() => navigate('/drills')} />
              ))}
            </Panel>
          )}
        </>
      )}

      <div className="u-gold-line" />
      <div className="u-center">
        <Button size="lg" onClick={() => navigate('/session')}>{t('dashboard.logNewSession')}</Button>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────

function StatChart({ data, dataKey, color, label, refLine }: {
  data: any[]; dataKey: string; color: string; label: string; refLine?: number;
}) {
  return (
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey="session" tick={{ fontFamily: 'var(--font-mono)', fontSize: 10, fill: '#6b6358' }} tickLine={false} />
        <YAxis tick={{ fontFamily: 'var(--font-mono)', fontSize: 10, fill: '#6b6358' }} tickLine={false} />
        <Tooltip
          contentStyle={{ background: '#071f16', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}
          labelStyle={{ color: '#b8b0a0' }}
          itemStyle={{ color }}
          formatter={(v: number) => [v, label]}
        />
        {refLine !== undefined && <ReferenceLine y={refLine} stroke="rgba(201,168,76,0.4)" strokeDasharray="4 4" />}
        <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={{ r: 3, fill: color, strokeWidth: 0 }} activeDot={{ r: 5, fill: color }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function ProgressRow({ label, current, target, unit, color }: {
  label: string; current: number; target: number; unit: string; color: string;
}) {
  const pct = Math.min(Math.round((current / Math.max(target, 1)) * 100), 100);
  return (
    <div className="progress-row">
      <div className="progress-row__labels">
        <span className="progress-row__label">{label}</span>
        <span className="progress-row__label">{current}{unit} / {target}{unit}</span>
      </div>
      <div className="progress-row__track">
        <div className="progress-row__fill" style={{ width: `${pct}%`, background: `linear-gradient(to right, ${color}88, ${color})` }} />
      </div>
    </div>
  );
}